import { useState } from 'react'
import type { FreshnessTag, IProfileAttributeSimilarMatch, IQuestionBankEntryDetail } from '@brushfire/types'
import { ApiError } from '@/api/http/types'
import { suggestProfileAttributeName } from '@brushfire/utils/profile-freshness'
import { fetchQuestionBankEntryById } from '@/features/question-bank/api/question-bank-queries'
import { builderQuestionToBankPayload } from '@/features/question-bank/lib/questionBankMappers'
import type { BuilderQuestion } from './types'
import {
  createProfileAttribute,
  findSimilarProfileAttribute,
  isProfileBuilderType,
  listProfileAttributes,
  updateProfileAttribute,
  type ProfileLink,
} from './profile-attributes.api'

export type ProfileUpdateDialogMode = 'confirm' | 'similar' | 'update' | null

export function profileUpdateDialogTestId(mode: ProfileUpdateDialogMode): string {
  if (mode === 'similar') return 'profile-update-similar-dialog'
  if (mode === 'update') return 'profile-update-edit-dialog'
  return 'profile-update-confirm-dialog'
}

function profileSaveError(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status === 409 && err.message.trim()) {
    return err.message
  }
  return fallback
}

type FlowParams = Readonly<{
  question: BuilderQuestion
  onChange: (patch: Partial<BuilderQuestion>) => void
  onUseBankEntry: (entry: IQuestionBankEntryDetail, link: ProfileLink) => void
}>

export function useProfileUpdateQuestionFlow({ question, onChange, onUseBankEntry }: FlowParams) {
  const [mode, setMode] = useState<ProfileUpdateDialogMode>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [match, setMatch] = useState<IProfileAttributeSimilarMatch | null>(null)
  const [name, setName] = useState('')
  const [editingAttributeId, setEditingAttributeId] = useState<string | null>(null)
  const [freshnessTag, setFreshnessTag] = useState<FreshnessTag>('PERM')
  const active = question.isProfileUpdate === true
  const profileAllowed = isProfileBuilderType(question.type)

  const close = () => {
    setMode(null)
    setError(null)
    setBusy(false)
    setEditingAttributeId(null)
  }

  const beginCreate = () => {
    onChange({ isProfileUpdate: true, fromQuestionBank: true })
    setEditingAttributeId(null)
    setName(suggestProfileAttributeName(question.questionText))
    setFreshnessTag('PERM')
    setMode('confirm')
  }

  const beginUpdate = async (attributeId: string) => {
    setEditingAttributeId(attributeId)
    setBusy(true)
    setError(null)
    try {
      const rows = await listProfileAttributes()
      const current = rows.find((row) => row.id.toLowerCase() === attributeId.toLowerCase())
      if (!current) {
        setName('')
        setError('Could not load this profile attribute.')
      } else {
        setName(current.name)
      }
    } catch (err) {
      setName('')
      setError(profileSaveError(err, 'Could not load this profile attribute.'))
    } finally {
      setBusy(false)
    }
    setMode('update')
  }

  const attributeIdFromBankEntry = (entry: IQuestionBankEntryDetail): string | null => {
    const linked = entry.profileAttributeId?.trim()
    if (!linked) return null
    onChange({
      isProfileUpdate: true,
      fromQuestionBank: true,
      profileAttributeId: linked,
      questionBankEntryId: entry.id,
    })
    return linked
  }

  const open = () => {
    if (!profileAllowed) return
    setError(null)
    const linkedId = question.profileAttributeId?.trim()
    if (linkedId) {
      Promise.resolve(beginUpdate(linkedId)).catch(() => undefined)
      return
    }
    const bankEntryId = question.questionBankEntryId?.trim()
    if (!bankEntryId) {
      beginCreate()
      return
    }
    Promise.resolve(
      (async () => {
        setBusy(true)
        try {
          const entry = await fetchQuestionBankEntryById(bankEntryId)
          const linked = attributeIdFromBankEntry(entry)
          if (linked) {
            await beginUpdate(linked)
            return
          }
        } catch {
          if (question.isProfileUpdate === true) {
            setEditingAttributeId(null)
            setName('')
            setError('Could not load this profile question.')
            setMode('update')
            setBusy(false)
            return
          }
        }
        setBusy(false)
        beginCreate()
      })(),
    ).catch(() => undefined)
  }

  const cancelConfirm = () => {
    onChange({
      isProfileUpdate: false,
      fromQuestionBank: Boolean(question.questionBankEntryId),
    })
    close()
  }

  const continueConfirm = async () => {
    const prompt = question.questionText.trim()
    const trimmed = name.trim()
    if (!prompt) {
      setError('Add question text before marking this as a profile update.')
      return
    }
    if (!trimmed) {
      setError('Attribute name is required.')
      return
    }
    setBusy(true)
    setError(null)
    let found: Awaited<ReturnType<typeof findSimilarProfileAttribute>> = null
    try {
      found = await findSimilarProfileAttribute({
        prompt,
        attributeName: trimmed,
      })
    } catch {
      found = null
    } finally {
      setBusy(false)
    }
    if (found) {
      setMatch(found)
      setMode('similar')
      return
    }

    setBusy(true)
    try {
      const payload = builderQuestionToBankPayload(question)
      const created = await createProfileAttribute({
        name: trimmed,
        freshnessTag,
        prompt: payload.prompt,
        type: payload.type,
        question: payload.question,
      })
      onChange({
        isProfileUpdate: true,
        fromQuestionBank: true,
        profileAttributeId: created.id,
        questionBankEntryId: created.questionBankEntryId ?? undefined,
        freshnessTag: created.freshnessTag,
        systemKey: created.systemKey,
      })
      close()
    } catch (err) {
      setError(profileSaveError(err, 'Could not save this profile attribute. Please try again.'))
    } finally {
      setBusy(false)
    }
  }

  const applySimilarMatch = async () => {
    if (!match) return
    setBusy(true)
    setError(null)
    try {
      const entry = await fetchQuestionBankEntryById(match.questionBankEntryId)
      onUseBankEntry(entry, {
        profileAttributeId: match.profileAttributeId,
        freshnessTag: match.freshnessTag,
        systemKey: match.systemKey,
        questionBankEntryId: match.questionBankEntryId,
      })
      close()
    } catch {
      setError('Could not load the existing profile question.')
      setBusy(false)
    }
  }

  const saveUpdate = async () => {
    const attributeId = editingAttributeId ?? question.profileAttributeId?.trim()
    if (!attributeId) return
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Attribute name is required.')
      return
    }
    const payload = builderQuestionToBankPayload(question)
    setBusy(true)
    setError(null)
    try {
      const updated = await updateProfileAttribute(attributeId, {
        name: trimmed,
        prompt: payload.prompt,
        type: payload.type,
        question: payload.question,
      })
      onChange({
        isProfileUpdate: true,
        freshnessTag: updated.freshnessTag,
        systemKey: updated.systemKey,
        questionBankEntryId: updated.questionBankEntryId ?? question.questionBankEntryId,
      })
      close()
    } catch (err) {
      setError(profileSaveError(err, 'Could not update this profile question.'))
      setBusy(false)
    }
  }

  return {
    active,
    profileAllowed,
    mode,
    busy,
    error,
    match,
    name,
    setName,
    freshnessTag,
    setFreshnessTag,
    question,
    open,
    close,
    cancelConfirm,
    continueConfirm,
    applySimilarMatch,
    saveUpdate,
  }
}
